import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, ScrollText, Receipt, Users, FileText,
  Bell, Brain, Settings, LogOut, Building2, X, LayoutGrid, ClipboardList, Database
} from 'lucide-react';

const navSections = [
  {
    label: 'OVERVIEW',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'manager', 'accountant'] },
      { to: '/floor-plan', icon: LayoutGrid, label: 'Floor Plan' },
    ],
  },
  {
    label: 'MANAGEMENT',
    items: [
      { to: '/leases', icon: ScrollText, label: 'Leases', roles: ['admin', 'manager', 'accountant', 'viewer'] },
      { to: '/invoices', icon: Receipt, label: 'Invoices', roles: ['admin', 'manager', 'accountant', 'viewer'] },
      { to: '/clients', icon: Users, label: 'Clients', roles: ['admin', 'manager', 'accountant', 'viewer', 'chef_rayon'] },
      { to: '/commercial-agreement', icon: ClipboardList, label: 'Agreements' },
      { to: '/documents', icon: FileText, label: 'Documents', roles: ['admin', 'manager', 'accountant', 'viewer'] },
      { to: '/notifications', icon: Bell, label: 'Notifications', roles: ['admin', 'manager', 'accountant', 'viewer'] },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { to: '/ai-analyzer', icon: Brain, label: 'AI Analyzer', roles: ['admin', 'manager', 'viewer'] },
      { to: '/admin', icon: Settings, label: 'Admin', roles: ['admin'] },
      { to: '/admin/data-sync', icon: Database, label: 'Data Sync', roles: ['admin'] },
    ],
  },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      onClose?.();
    }
  };

  return (
    <aside className={`sidebar flex flex-col ${isOpen ? 'open' : ''}`} data-testid="sidebar-nav">
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <span className="text-lg font-semibold tracking-tight text-slate-900">
            SimlaBDT
          </span>
        </div>
        <button 
          className="md:hidden text-slate-400 hover:text-slate-600 p-1 transition-colors"
          onClick={onClose}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {navSections.map((section) => {
          const visibleItems = section.items.filter(
            (item) => !item.roles || item.roles.includes(user?.role)
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label}>
              <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {section.label}
              </p>
              <div className="space-y-1">
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.to;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick}
                      className={`sidebar-link ${isActive ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                      <span>{item.label}</span>
                      {isActive && <div className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-600" />}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <span className="text-sm font-medium">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
            <p className="truncate text-xs text-slate-400 capitalize">{user?.role === 'chef_rayon' ? 'Chef Rayon' : user?.role}</p>
          </div>
        </div>
        <button
          onClick={() => { logout(); onClose?.(); }}
          className="sidebar-link w-full text-slate-400 hover:text-red-500 hover:bg-red-50"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};
